//#region imports
import React from 'react';

// redux
import { useSelector, useDispatch } from 'react-redux';
import {
    addFlightPassenger,
    dismissPossibleDuplicates,
    selectAddFlightPassengerStatus,
    selectAddFlightPassengerPossibleDuplicates,
    selectAddFlightPassengerLastRequest,
} from '_redux/slices/timeline/timeline-bookings';

// bootstrap
import { Button, Modal } from 'react-bootstrap';

// styling
import { Colors } from '_styles';
//#endregion

const SOURCE_LABELS = {
    SITA: 'Airline message (SITA)',
    APP: 'Passenger app',
    PORTAL: 'Portal',
    WN: 'Southwest',
    NK: 'Spirit',
};

const formatCreated = (iso) => {
    const date = iso ? new Date(iso) : null;
    if (!date || isNaN(date.getTime())) return '';
    return date.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const flightLabel = (booking) => `${booking?.flightDetails?.carrierCode || ''}${booking?.flightDetails?.flightNumber || ''}`;

// the server also matches the same flight a day either side (a delay past midnight
// keeps yesterday's date), so say which day when it isn't the one being booked
const otherFlightDay = (match, booking) => {
    const requested = booking?.flightDetails?.departureDate || booking?.flightDetails?.arrivalDate;
    if (!match.departureDate || match.departureDate === requested) return '';
    const day = new Date(`${match.departureDate}T00:00:00Z`);
    if (isNaN(day.getTime())) return '';
    return day.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' });
};

/**
 * Shown when the server thinks a passenger being added is already booked on
 * the flight under another id (SITA, app or an earlier portal entry). Staff
 * either go back or resend the same submission with allowPossibleDuplicate.
 * Renders as a modal, or inline when it sits inside another modal.
 */
const PossibleDuplicateWarning = ({ inline = false }) => {
    const dispatch = useDispatch();
    const status = useSelector(selectAddFlightPassengerStatus);
    const duplicates = useSelector(selectAddFlightPassengerPossibleDuplicates);
    const lastRequest = useSelector(selectAddFlightPassengerLastRequest);

    const show = status === 'possible_duplicate' && duplicates.length > 0 && !!lastRequest;
    if (!show) return null;

    const total = lastRequest.bookings?.length || 0;
    const created = total - duplicates.length;

    const handleCancel = () => dispatch(dismissPossibleDuplicates());

    const handleCreateAnyway = () => {
        dispatch(addFlightPassenger({
            ...lastRequest,
            bookings: lastRequest.bookings.map((booking) => ({ ...booking, allowPossibleDuplicate: true })),
        }));
    };

    const body = (
        <>
            <p style={styles.intro}>
                {duplicates.length === 1
                    ? 'This passenger looks like they are already booked on this flight.'
                    : 'These passengers look like they are already booked on this flight.'}
                {' '}Check the existing booking before creating another, or the passenger will be assigned twice.
            </p>

            {duplicates.map(({ booking, matches }) => (
                <div key={`${booking.name}-${flightLabel(booking)}`} style={styles.group}>
                    <div style={styles.groupTitle}>
                        {booking.name} <span style={styles.flight}>· {flightLabel(booking)}</span>
                    </div>
                    {matches.map((match) => (
                        <div key={match._id} style={styles.match}>
                            <div style={styles.matchName}>
                                {match.name}
                                {otherFlightDay(match, booking) && <span style={styles.otherDay}> · flight on {otherFlightDay(match, booking)}</span>}
                            </div>
                            <div style={styles.matchMeta}>
                                {SOURCE_LABELS[match.source] || match.source}
                                {match.assistance?.length > 0 && ` · ${match.assistance.join(', ')}`}
                                {formatCreated(match.dateCreated) && ` · created ${formatCreated(match.dateCreated)}`}
                            </div>
                        </div>
                    ))}
                </div>
            ))}

            {created > 0 && (
                <p style={styles.note}>
                    The other {created === 1 ? 'passenger' : `${created} passengers`} in this submission
                    {created === 1 ? ' has' : ' have'} already been created.
                </p>
            )}
        </>
    );

    const actions = (
        <>
            <Button variant='outline-secondary' onClick={handleCancel} style={styles.button}>
                Go back
            </Button>
            <Button variant='warning' onClick={handleCreateAnyway} style={styles.button}>
                Create anyway
            </Button>
        </>
    );

    if (inline) {
        return (
            <div style={styles.inline} role='alert'>
                <div style={styles.inlineTitle}>Possible duplicate booking</div>
                {body}
                <div style={styles.inlineActions}>{actions}</div>
            </div>
        );
    }

    return (
        <Modal show onHide={handleCancel} centered>
            <Modal.Header closeButton>
                <Modal.Title style={styles.modalTitle}>Possible duplicate booking</Modal.Title>
            </Modal.Header>
            <Modal.Body>{body}</Modal.Body>
            <Modal.Footer>{actions}</Modal.Footer>
        </Modal>
    );
};

const styles = {
    intro: {
        fontSize: 14,
        marginBottom: 12,
    },
    group: {
        marginBottom: 12,
    },
    groupTitle: {
        fontSize: 14,
        fontWeight: 600,
        marginBottom: 6,
    },
    flight: {
        fontWeight: 400,
        color: Colors.GRAY_DARK,
    },
    match: {
        padding: '8px 12px',
        borderLeft: `3px solid ${Colors.ORANGE}`,
        background: 'rgba(235, 183, 28, 0.12)',
        borderRadius: 6,
        marginBottom: 6,
    },
    matchName: {
        fontSize: 14,
        fontWeight: 500,
    },
    otherDay: {
        fontWeight: 600,
        color: Colors.DARK_RED,
    },
    matchMeta: {
        fontSize: 12,
        color: Colors.GRAY_DARK,
    },
    note: {
        fontSize: 12,
        color: Colors.GRAY_DARK,
        marginBottom: 0,
    },
    button: {
        borderRadius: 8,
        fontSize: 14,
        fontWeight: 500,
    },
    modalTitle: {
        fontSize: 18,
        fontWeight: 600,
    },
    inline: {
        border: `1px solid ${Colors.ORANGE}`,
        borderRadius: 10,
        padding: 16,
        marginBottom: 16,
    },
    inlineTitle: {
        fontSize: 15,
        fontWeight: 600,
        marginBottom: 8,
    },
    inlineActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        gap: 8,
        marginTop: 12,
    },
};

export default PossibleDuplicateWarning;
